/* CalPerc quote list — browser-side "Add to Quote" that feeds the
   rental quote form. Items live in localStorage; the quote page reads
   them into the instruments field so submissions name exact gear. */
(() => {
  const KEY = 'cpla_quote_v1';

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  };
  const write = (items) => {
    localStorage.setItem(KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('cpla:quote', { detail: { items } }));
  };

  window.CplaQuote = {
    items: read,
    count: () => read().length,
    add(item) {
      const items = read();
      const qty = Math.max(1, parseInt(item.qty, 10) || 1);
      const existing = items.find((i) => i.handle === item.handle);
      if (existing) {
        existing.qty = qty;
      } else {
        items.push({ ...item, qty });
      }
      write(items);
      return items;
    },
    setQty(handle, qty) {
      const items = read();
      const item = items.find((i) => i.handle === handle);
      if (!item) return;
      const ceiling = parseInt(item.maxQty, 10) || 999;
      item.qty = Math.min(ceiling, Math.max(1, parseInt(qty, 10) || 1));
      write(items);
    },
    remove(handle) {
      write(read().filter((i) => i.handle !== handle));
    },
    clear() {
      write([]);
    },
  };

  // --- Header pill: show count, hide at zero ---
  const pills = () => document.querySelectorAll('[data-quote-pill]');
  const renderPills = () => {
    const n = window.CplaQuote.count();
    pills().forEach((el) => {
      // Empty state stays in the DOM as a "Get Quote" CTA — CSS decides where it
      // shows (phones yes, desktop no, since Reserve covers it there).
      el.hidden = false;
      el.classList.toggle('cpla-header__quote-pill--empty', n === 0);
      const badge = el.querySelector('[data-quote-count]');
      if (badge) {
        badge.textContent = n;
        badge.hidden = n === 0;
      }
      const label = el.querySelector('[data-quote-label]');
      if (label) label.textContent = n === 0 ? 'Get Quote' : 'Quote';
    });
    // The Quote pill replaces the Reserve button once the list has items —
    // both lead to the same place, so only one shows at a time.
    document.querySelectorAll('[data-reserve-cta]').forEach((el) => {
      el.hidden = n > 0;
    });
  };

  // --- Product page: Add to Quote button ---
  const bindAddButtons = () => {
    document.querySelectorAll('[data-quote-add]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const qtyInput = document.querySelector('[data-quote-qty]');
        const qty = qtyInput ? clampQty(qtyInput) : 1;
        window.CplaQuote.add({
          handle: btn.dataset.handle,
          title: btn.dataset.title,
          url: btn.dataset.url,
          qty: qty,
          maxQty: qtyInput ? parseInt(qtyInput.max, 10) || null : null,
        });
        btn.classList.add('is-added');
        const label = btn.querySelector('[data-quote-add-label]');
        if (label) label.textContent = qty > 1 ? 'Added ×' + qty + ' ✓' : 'Added to quote ✓';
      });
    });
  };

  // --- PDP quantity stepper (capped by the product's Max quantity metafield) ---
  const clampQty = (input) => {
    const max = parseInt(input.max, 10) || 999;
    const value = Math.min(max, Math.max(1, parseInt(input.value, 10) || 1));
    input.value = value;
    // Tell the customer when they've asked for the whole inventory.
    const note = document.querySelector('[data-qty-max-note]');
    if (note) note.hidden = value < max;
    return value;
  };
  const bindQtySteppers = () => {
    document.querySelectorAll('[data-qty-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.closest('[data-quote-qty-wrap]')?.querySelector('[data-quote-qty]');
        if (!input) return;
        input.value = (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyStep, 10);
        clampQty(input);
      });
    });
    document.querySelectorAll('[data-quote-qty]').forEach((input) => {
      input.addEventListener('change', () => clampQty(input));
      input.addEventListener('blur', () => clampQty(input));
    });
  };

  // --- "Often rented with" pills: one-tap add, tap again to remove ---
  const paintQuickAdds = () => {
    const handles = window.CplaQuote.items().map((i) => i.handle);
    document.querySelectorAll('[data-quote-quickadd]').forEach((btn) => {
      const added = handles.includes(btn.dataset.handle);
      btn.classList.toggle('is-in-quote', added);
      btn.setAttribute('aria-label', (added ? 'Remove ' : 'Add ') + btn.dataset.title + (added ? ' from quote' : ' to quote'));
    });
  };
  const bindQuickAdds = () => {
    document.querySelectorAll('[data-quote-quickadd]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const inQuote = window.CplaQuote.items().some((i) => i.handle === btn.dataset.handle);
        if (inQuote) {
          window.CplaQuote.remove(btn.dataset.handle);
        } else {
          window.CplaQuote.add({
            handle: btn.dataset.handle,
            title: btn.dataset.title,
            url: btn.dataset.url,
          });
        }
      });
    });
  };

  // --- Quote page: render list + sync into the instruments textarea ---
  const AUTO_HEADER = 'Requested items:';
  const syncTextarea = () => {
    const field = document.getElementById('cf-instruments');
    if (!field) return;
    const items = window.CplaQuote.items();
    const auto = items.length
      ? AUTO_HEADER + '\n' + items.map((i) => '- ' + i.title + (i.qty > 1 ? ' × ' + i.qty : '')).join('\n')
      : '';
    // Preserve anything the customer typed after the auto block.
    const current = field.value;
    const idx = current.indexOf(AUTO_HEADER);
    let manual = current;
    if (idx !== -1) {
      const after = current.slice(idx);
      const endOfAuto = after.split('\n').filter((l) => l.startsWith('-') || l.startsWith(AUTO_HEADER)).join('\n');
      manual = (current.slice(0, idx) + after.slice(endOfAuto.length)).trim();
    }
    field.value = [auto, manual].filter(Boolean).join('\n\n');
  };

  const renderQuoteList = () => {
    const wrap = document.querySelector('[data-quote-list]');
    if (!wrap) return;
    const items = window.CplaQuote.items();
    wrap.hidden = items.length === 0;
    const empty = document.querySelector('[data-quote-empty]');
    if (empty) empty.hidden = items.length > 0;
    const ul = wrap.querySelector('ul');
    if (!ul) return;
    ul.innerHTML = '';
    items.forEach((i) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = i.url;
      a.textContent = i.title;

      const qty = Math.max(1, parseInt(i.qty, 10) || 1);
      const qtyWrap = document.createElement('span');
      qtyWrap.className = 'cpla-quote-qty font-mono';
      const dec = document.createElement('button');
      dec.type = 'button';
      dec.textContent = '−';
      dec.setAttribute('aria-label', 'Decrease quantity of ' + i.title);
      dec.addEventListener('click', () => {
        if (qty <= 1) return;
        window.CplaQuote.setQty(i.handle, qty - 1);
      });
      const n = document.createElement('span');
      n.textContent = '×' + qty;
      const inc = document.createElement('button');
      inc.type = 'button';
      inc.textContent = '+';
      inc.setAttribute('aria-label', 'Increase quantity of ' + i.title);
      inc.addEventListener('click', () => {
        window.CplaQuote.setQty(i.handle, qty + 1);
      });
      qtyWrap.appendChild(dec);
      qtyWrap.appendChild(n);
      qtyWrap.appendChild(inc);

      const rm = document.createElement('button');
      rm.type = 'button';
      rm.setAttribute('aria-label', 'Remove ' + i.title);
      rm.textContent = '×';
      rm.addEventListener('click', () => {
        window.CplaQuote.remove(i.handle);
      });
      li.appendChild(a);
      li.appendChild(qtyWrap);
      li.appendChild(rm);
      ul.appendChild(li);
    });
    syncTextarea();
  };

  document.addEventListener('cpla:quote', () => {
    renderPills();
    renderQuoteList();
    paintQuickAdds();
  });

  document.addEventListener('DOMContentLoaded', () => {
    renderPills();
    bindAddButtons();
    bindQtySteppers();
    bindQuickAdds();
    paintQuickAdds();
    renderQuoteList();
    // Successful submit → clear the list
    if (document.querySelector('[data-quote-submitted]')) {
      window.CplaQuote.clear();
    }
  });
})();
